import React from 'react';

const SPACING = {
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem'
};

const CHAKRA_COLORS = {
  gray: {
    50: '#F7FAFC',
    100: '#EDF2F7',
    200: '#E2E8F0',
    300: '#CBD5E0',
    400: '#A0AEC0',
    500: '#718096',
    600: '#4A5568',
    700: '#2D3748',
    800: '#1A202C',
    900: '#171923'
  },
  red: { 500: '#E53E3E' },
  green: { 500: '#38A169' },
  blue: { 500: '#3182CE' },
  purple: { 500: '#805AD5' },
  orange: { 500: '#DD6B20' }
};

const STYLE_PROP_MAP = {
  m: 'margin',
  mt: 'marginTop',
  mb: 'marginBottom',
  ml: 'marginLeft',
  mr: 'marginRight',
  mx: 'marginInline',
  my: 'marginBlock',
  p: 'padding',
  pt: 'paddingTop',
  pb: 'paddingBottom',
  pl: 'paddingLeft',
  pr: 'paddingRight',
  px: 'paddingInline',
  py: 'paddingBlock',
  paddingX: 'paddingInline',
  paddingY: 'paddingBlock',
  w: 'width',
  h: 'height',
  minH: 'minHeight',
  minW: 'minWidth',
  maxW: 'maxWidth',
  maxH: 'maxHeight',
  bg: 'backgroundColor',
  bgColor: 'backgroundColor',
  background: 'background',
  backgroundColor: 'backgroundColor',
  color: 'color',
  fontSize: 'fontSize',
  fontWeight: 'fontWeight',
  textAlign: 'textAlign',
  lineHeight: 'lineHeight',
  borderRadius: 'borderRadius',
  rounded: 'borderRadius',
  border: 'border',
  borderColor: 'borderColor',
  borderWidth: 'borderWidth',
  borderBottom: 'borderBottom',
  borderTop: 'borderTop',
  boxShadow: 'boxShadow',
  shadow: 'boxShadow',
  overflow: 'overflow',
  overflowX: 'overflowX',
  overflowY: 'overflowY',
  position: 'position',
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
  zIndex: 'zIndex',
  opacity: 'opacity',
  cursor: 'cursor',
  whiteSpace: 'whiteSpace',
  textOverflow: 'textOverflow',
  flex: 'flex',
  flexGrow: 'flexGrow',
  flexShrink: 'flexShrink',
  flexDir: 'flexDirection',
  flexDirection: 'flexDirection',
  gap: 'gap',
  alignSelf: 'alignSelf',
  textDecoration: 'textDecoration',
  fontStyle: 'fontStyle',
  wordBreak: 'wordBreak',
  overflowWrap: 'overflowWrap',
  gridColumn: 'gridColumn',
  gridRow: 'gridRow',
  objectFit: 'objectFit',
  transform: 'transform',
  visibility: 'visibility',
  display: 'display',
  width: 'width',
  height: 'height'
};

const FLEX_PROP_MAP = {
  direction: 'flexDirection',
  dir: 'flexDirection',
  align: 'alignItems',
  alignItems: 'alignItems',
  justify: 'justifyContent',
  justifyContent: 'justifyContent',
  wrap: 'flexWrap',
  flexWrap: 'flexWrap'
};

const HEADING_SIZES = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem'
};

export function resolveColorValue(value) {
  if (typeof value !== 'string') return value;
  if (!value.includes('.')) return value;
  const [palette, shade] = value.split('.');
  return CHAKRA_COLORS[palette]?.[shade] ?? value;
}

export function resolveCSSValue(value) {
  if (value == null || value === false) return undefined;
  if (typeof value === 'object') {
    return resolveCSSValue(value.base ?? value.md ?? value.lg ?? Object.values(value)[0]);
  }
  if (typeof value === 'number' && SPACING[value] != null) return SPACING[value];
  if (typeof value === 'number') return `${value}px`;
  return resolveColorValue(value);
}

export function splitStyleProps(props = {}, { isFlex = false } = {}) {
  const style = {};
  const rest = { ...props };

  Object.entries(STYLE_PROP_MAP).forEach(([prop, cssProp]) => {
    if (rest[prop] != null) {
      style[cssProp] = resolveCSSValue(rest[prop]);
      delete rest[prop];
    }
  });

  if (isFlex) {
    Object.entries(FLEX_PROP_MAP).forEach(([prop, cssProp]) => {
      if (rest[prop] != null) {
        style[cssProp] = resolveCSSValue(rest[prop]);
        delete rest[prop];
      }
    });
  }

  if (rest.size && !rest.fontSize) {
    style.fontSize = HEADING_SIZES[rest.size] ?? rest.size;
    delete rest.size;
  }

  if (rest.spacing != null) {
    style.gap = resolveCSSValue(rest.spacing);
    delete rest.spacing;
  }

  if (rest.isTruncated) {
    style.overflow = 'hidden';
    style.textOverflow = 'ellipsis';
    style.whiteSpace = 'nowrap';
    delete rest.isTruncated;
  }

  const sx = rest.sx;
  delete rest._hover;
  delete rest._focus;
  delete rest._active;
  delete rest._disabled;
  delete rest.sx;
  delete rest.colorScheme;
  delete rest.variant;

  return { style, rest, sx };
}

export function createStyledComponent(defaultTag, { isFlex = false, defaultStyle = {} } = {}) {
  return function StyledComponent({ as, className, style, children, sx, ...props }) {
    const Tag = as || defaultTag;
    const { style: propStyle, rest, sx: sxProp } = splitStyleProps(props, { isFlex });
    const sxStyle = typeof (sx || sxProp) === 'object' ? sx || sxProp : {};
    return (
      <Tag
        className={className}
        style={{ ...defaultStyle, ...propStyle, ...sxStyle, ...style }}
        {...rest}
      >
        {children}
      </Tag>
    );
  };
}
